import { useEffect, useState } from "react";
import { getAuthHeaders, triggerUnauthorized } from "./token";

export type AuthMe = {
  sub: string | null;
  email: string | null;
  enabled: boolean;
};

export type AuthMeState = {
  data: AuthMe | null;
  loading: boolean;
  error: boolean;
};

/**
 * Fetches GET /auth/me with the current token. Returns { data, loading, error }.
 * On 401, triggers unauthorized (redirect to login). Option A: backend returns 200 with enabled in body.
 */
export function useAuthMe(token: string | null): AuthMeState {
  const [data, setData] = useState<AuthMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setData(null);
      setError(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetch("/auth/me", { headers: getAuthHeaders() })
      .then((res) => {
        if (cancelled) return;
        if (res.status === 401) {
          triggerUnauthorized();
          setError(true);
          setLoading(false);
          return;
        }
        if (!res.ok) {
          setError(true);
          setLoading(false);
          return;
        }
        return res.json();
      })
      .then((body: { sub?: string; email?: string; enabled?: boolean } | undefined) => {
        if (cancelled || body === undefined) return;
        setData({
          sub: body.sub ?? null,
          email: body.email ?? null,
          enabled: body.enabled !== false,
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { data, loading, error };
}
